import { useState } from "react";
import Layout from "../../../components/Layout";
import { HiPlus, HiPencil, HiTrash, HiSearch, HiUserGroup } from "react-icons/hi";
import CategoryForm from "./CategoryForm";
import {
  CustomTable, CustomTableHead, CustomTableBody,
  CustomTableHeadCell, CustomTableRow, CustomTableCell,
} from "../../../components/CustomTable";
import { CustomButton, CustomInput } from "../../../components/FormFields";
import { StatusBadge } from "../../../components/StatusBadge";
import { INITIAL_CATEGORIES } from "../../../data/employeeCategories";

const TYPE_COLOR = {
  Permanent: "success",
  Temporary: "warning",
  Contract:  "blue",
  Intern:    "indigo",
  Probation: "gray",
};

export default function Categories() {
  const [categories, setCategories] = useState(INITIAL_CATEGORIES);
  const [showForm, setShowForm]     = useState(false);
  const [editData, setEditData]     = useState(null);
  const [search, setSearch]         = useState("");

  const filtered = categories.filter((c) =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.employmentType.toLowerCase().includes(search.toLowerCase())
  );

  function handleSave(cat) {
    setCategories((prev) =>
      prev.some((c) => c.id === cat.id)
        ? prev.map((c) => (c.id === cat.id ? cat : c))
        : [...prev, cat]
    );
  }

  function handleDelete(id) {
    setCategories((prev) => prev.filter((c) => c.id !== id));
  }

  return (
    <Layout>
      <div className="flex flex-col gap-6">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Employee Categories</h1>
            <p className="mt-1 text-sm text-gray-500">
              Define employee groups — each category can be linked to a leave policy
            </p>
          </div>
          <CustomButton color="purple" onClick={() => { setEditData(null); setShowForm(true); }}>
            <HiPlus className="mr-2 h-4 w-4" /> Create Category
          </CustomButton>
        </div>

        {/* Search */}
        <div className="w-full max-w-sm">
          <CustomInput
            icon={HiSearch}
            placeholder="Search categories..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Table */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <CustomTable hoverable>
              <CustomTableHead>
                <CustomTableRow>
                  <CustomTableHeadCell>#</CustomTableHeadCell>
                  <CustomTableHeadCell>Category Name</CustomTableHeadCell>
                  <CustomTableHeadCell>Description</CustomTableHeadCell>
                  <CustomTableHeadCell>Employment Type</CustomTableHeadCell>
                  <CustomTableHeadCell className="text-center">Actions</CustomTableHeadCell>
                </CustomTableRow>
              </CustomTableHead>
              <CustomTableBody className="divide-y divide-gray-100 dark:divide-gray-700">
                {filtered.length === 0 ? (
                  <CustomTableRow>
                    <CustomTableCell colSpan={5} className="py-10 text-center text-sm text-gray-400">
                      No categories found.
                    </CustomTableCell>
                  </CustomTableRow>
                ) : filtered.map((cat, i) => (
                  <CustomTableRow key={cat.id} className="hover:bg-purple-50 dark:hover:bg-gray-700">
                    <CustomTableCell className="text-sm text-gray-400">{i + 1}</CustomTableCell>
                    <CustomTableCell className="text-sm font-medium text-gray-800 dark:text-gray-200">
                      <div className="flex items-center gap-2">
                        <HiUserGroup className="h-4 w-4 text-purple-400 flex-shrink-0" />
                        {cat.name}
                      </div>
                    </CustomTableCell>
                    <CustomTableCell className="text-sm text-gray-500 dark:text-gray-400">
                      {cat.description || <span className="text-gray-300">—</span>}
                    </CustomTableCell>
                    <CustomTableCell>
                      <StatusBadge color={TYPE_COLOR[cat.employmentType] ?? "gray"} className="w-fit text-xs">
                        {cat.employmentType}
                      </StatusBadge>
                    </CustomTableCell>
                    <CustomTableCell>
                      <div className="flex justify-center gap-2">
                        <CustomButton size="xs" color="blue" outline pill
                          onClick={() => { setEditData(cat); setShowForm(true); }}>
                          <HiPencil className="h-3.5 w-3.5" />
                        </CustomButton>
                        <CustomButton size="xs" color="failure" outline pill
                          onClick={() => handleDelete(cat.id)}>
                          <HiTrash className="h-3.5 w-3.5" />
                        </CustomButton>
                      </div>
                    </CustomTableCell>
                  </CustomTableRow>
                ))}
              </CustomTableBody>
            </CustomTable>
          </div>
        </div>
      </div>

      {showForm && (
        <CategoryForm
          closeForm={() => { setShowForm(false); setEditData(null); }}
          initialData={editData}
          onSave={handleSave}
        />
      )}
    </Layout>
  );
}
